import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { ajaxGetPurchaseOrderHistory } from '../../services/purchaseOrderHistoryService';
import LoadingSpinner from '../../components/LoadingSpinner';
import Page from '../../components/Page';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import { downloadFile } from '../../helpers';
import moment from 'moment';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import StyledCard from '../../components/StyledCard';
import StyledCardFormContent from '../../components/StyledCardFormContent';
import UtcDatePicker from '../../components/UtcDatePicker';
import PurchaseOrderParts from '../../components/PurchaseOrderParts';
import LoadingButton from '../../components/LoadingButton';
import { toDate, toDateTimeText} from '../../helpers';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getAllCurrencyList } from '../../helpers';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Chip from '@mui/material/Chip';

export default function PurchaseOrder(props) {
  const defaultData = {
    id: null,
    po: null,
    date: moment().hour(0).minute(0).second(0).millisecond(0).unix() + (moment().utcOffset() * 60),
    supplier: null,
    supplier_name: '',
    supplier_contact_person: '',
    supplier_2303: '',
    supplier_address: '',
    supplier_email: '',
    supplier_phone: '',
    requisitioner: '',
    po_number: '',
    po_date: moment().hour(0).minute(0).second(0).millisecond(0).unix() + (moment().utcOffset() * 60),
    due_date: moment().hour(0).minute(0).second(0).millisecond(0).unix() + (moment().utcOffset() * 60),
    purpose: '',
    department: '',
    terms: '',
    ship_to: '',
    price_quote: '',
    currency: 'USD',
    currency_rate: 1.0,
    status: 'draft',
    parts: []
  };
  const { PurchaseOrderId, PurchaseOrderHistoryId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseOrderHistory, setPurchaseOrderHistory] = useState(defaultData);

  const backUrl = `/purchaseorders/history/${PurchaseOrderId}`;

  useEffect(() => {
    const loadData = () => {
      ajaxGetPurchaseOrderHistory({PurchaseOrderHistoryId})
        .then((poRes) => {
          if (poRes && poRes.data) {
            setPurchaseOrderHistory(poRes.data);
          }
        })
        .catch((error) => {
          console.error('Error loading data:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    loadData();
  }, [PurchaseOrderId, PurchaseOrderHistoryId]);

  const allFields = useMemo(
    () => [
      {
        key: 'supplier_name',
        type: 'text',
        label: 'Supplier Name',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 12 }
      },
      {
        key: 'supplier_contact_person',
        type: 'text',
        label: 'Contact Person',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'supplier_2303',
        type: 'text',
        label: 'Supplier 2303/TIN#',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'supplier_email',
        type: 'text',
        label: 'Email',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'supplier_phone',
        type: 'text',
        label: 'Phone Number',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'supplier_address',
        type: 'text',
        label: 'Address',
        required: true,
        multiline: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'ship_to',
        type: 'text',
        label: 'Ship To',
        multiline: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'requisitioner',
        type: 'text',
        label: 'Requisitioner',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'purpose',
        type: 'text',
        label: 'Purpose',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        divider: true,
        gridProps: { xs: 12 }
      },
      {
        key: 'currency',
        type: 'autocomplete',
        label: 'Currency',
        dataset: getAllCurrencyList().map(c => ({ value: c, label: c })),
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'currency_rate',
        type: 'text',
        label: 'Currency Rate to USD',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'parts',
        type: 'component',
        component: PurchaseOrderParts,
        componentProps: {
          parts: purchaseOrderHistory.parts,
          currency: purchaseOrderHistory.currency,
          currencyRate: purchaseOrderHistory.currency_rate,
          updateParts: () => {},
          disabled: true,

        },
        gridProps: { xs: 12 }
      },
      {
        divider: true,
        gridProps: { xs: 12 }
      },
      {
        key: 'po_number',
        type: 'text',
        label: 'PO Number',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'po_date',
        type: 'date',
        label: 'PO Date',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'department',
        type: 'text',
        label: 'Department',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'due_date',
        type: 'date',
        label: 'Due Date',
        required: true,
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'terms',
        type: 'text',
        label: 'Terms',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'price_quote',
        type: 'text',
        label: 'Price Quote',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        divider: true,
        gridProps: { xs: 12 }
      },
      {
        key: 'prepared_by_name',
        type: 'text',
        label: 'Prepared By Name',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'prepared_by_position',
        type: 'text',
        label: 'Prepared By Position',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'approved_by_name',
        type: 'text',
        label: 'Approved By Name',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        key: 'approved_by_position',
        type: 'text',
        label: 'Approved By Position',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        divider: true,
        gridProps: { xs: 12 }
      },
      {
        key: 'status',
        type: 'list',
        label: 'Status',
        dataset: [
          { value: 'draft', label: 'Draft' },
          { value: 'waitingapproval', label: 'Waiting Approval' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'received', label: 'Received' },
          { value: 'cancelled', label: 'Cancelled' },
        ],
        disabled: true,
        gridProps: { xs: 12, md: 12 }
      },
      {
        divider: true,
        gridProps: { xs: 12 }
      },
      {
        key: 'shipping_cost',
        type: 'text',
        label: 'Shipping Cost',
        gridProps: { xs: 12, md: 6 },
        disabled: true,
        convertToUI: (v) => {
          if ( purchaseOrderHistory.shipping_cost_UI !== null && purchaseOrderHistory.shipping_cost_UI !== undefined ){
            return purchaseOrderHistory.shipping_cost_UI;
          }
          if (v === '' || v === null){
            return '';
          }
          return v / (purchaseOrderHistory.currency_rate || 1)
        },
        convertToDB: (v) => isNaN(parseFloat(v)) ? null : parseFloat(v) * (purchaseOrderHistory.currency_rate || 1),
      },
      {
        key: 'awb_number',
        type: 'text',
        label: 'AWB Number',
        disabled: true,
        gridProps: { xs: 12, md: 6 }
      },
      {
        divider: true,
        gridProps: { xs: 12 }
      },
    ],
    [purchaseOrderHistory.parts, purchaseOrderHistory.currency, purchaseOrderHistory.currency_rate, purchaseOrderHistory.shipping_cost_UI]
  );


  const getFieldValue = (key) => purchaseOrderHistory && purchaseOrderHistory[key] !== undefined && purchaseOrderHistory[key] !== null ? purchaseOrderHistory[key] : '';

  const buttonStyle = {
    height: '40px',
    width: '150px',
    fontSize: '12px'
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Page
      title={
        [
          `Purchase Order`,
          purchaseOrderHistory.po_number,
          toDateTimeText(purchaseOrderHistory.date),
          <Chip
            size="small"
            label={
              purchaseOrderHistory.status === 'draft' ? 'Draft' :
              purchaseOrderHistory.status === 'waitingapproval' ? 'Waiting Approval' :
              purchaseOrderHistory.status === 'approved' ? 'Approved' :
              purchaseOrderHistory.status === 'rejected' ? 'Rejected' :
              purchaseOrderHistory.status === 'received' ? 'Received' :
              purchaseOrderHistory.status === 'cancelled' ? 'Cancelled' :
              'Unknown'
            }
            color={
              purchaseOrderHistory.status === 'draft' ? 'default' : 
              purchaseOrderHistory.status === 'waitingapproval' ? 'warning' :
              purchaseOrderHistory.status === 'approved' ? 'success' : 
              purchaseOrderHistory.status === 'rejected' ? 'error' : 
              purchaseOrderHistory.status === 'received' ? 'info' : 
              purchaseOrderHistory.status === 'cancelled' ? 'error' :
              'default'
            }
          />
        ]
      }
      backButton={
        <IconButton
          color="primary"
          component={RouterLink}
          to={backUrl}
        >
          <ArrowBackIcon fontSize="medium" />
        </IconButton>
      }
    >
      <StyledCard>
        <StyledCardFormContent>
          <Grid container spacing={3}>
            {allFields.map((entry) => 
              <React.Fragment key={entry['key']}>
                { (entry['type'] === 'component') && 
                  <Grid item xs={12} {...entry['gridProps']}>
                    <entry.component {...entry.componentProps} />
                  </Grid>
                }
                {
                  (entry['type'] === 'text') &&
                  <Grid item xs={12} {...entry['gridProps']}>
                    <TextField
                      label={entry['label']}
                      type={entry['type']}
                      name={entry['key']}
                      size="small"
                      value={entry['convertToUI'] ? entry['convertToUI'](getFieldValue(entry['key'])) : getFieldValue(entry['key'])}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      disabled={!!entry.disabled}
                      autoFocus={!!entry.autoFocus}
                      {...entry.multiline ? { multiline: true, rows: entry.lineNum ? entry.lineNum : 4 } : {}}
                    />
                  </Grid>
                }
                {
                  entry['type'] === 'autocomplete' &&
                  <Grid item xs={12} {...entry['gridProps']}>
                    <FormControl fullWidth disabled={!!entry.disabled}>
                      <Autocomplete
                        sx={{ width: '100%' }}
                        size="small" 
                        value={(entry.dataset || []).find(o=>o.value === getFieldValue(entry['key'])) || null}
                        options={(entry.dataset || [])}
                        isOptionEqualToValue={(option, value) => value && option.value === value.value}
                        getOptionLabel={(option) => option.label !== option.value ? `${option.label} [${option.value}]` : option.label}
                        renderInput={(params) => <TextField {...params} label={entry['label']} disabled={!!entry.disabled}/>}
                      />
                    </FormControl>
                  </Grid>
                }
                {
                  entry['type'] === 'list' &&
                  <Grid item xs={12} {...entry['gridProps']}>
                    <FormControl fullWidth disabled={!!entry.disabled}>
                      <InputLabel id={`select-helper-label-${entry['key']}`}>{entry['label']}</InputLabel>
                      <Select
                        labelId={`select-helper-label-${entry['key']}`}
                        label={entry['label']}
                        type={entry['type']}
                        name={entry['key']}
                        size="small"
                        value={getFieldValue(entry['key'])}
                        sx={{ width: '100%' }}
                      >
                        { (entry.dataset || []).map( c =>
                          <MenuItem value={c.value}>{c.label}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                }
                {
                  entry['type'] === 'date' &&
                  <Grid item xs={12} {...entry['gridProps']}>
                    <UtcDatePicker
                      label={entry['label']}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                        },
                      }}
                      value={getFieldValue(entry['key']) ? moment(toDate(getFieldValue(entry['key']))).unix() : null}
                      disabled={!!entry.disabled}
                    />
                  </Grid>
                }
                {
                  entry['divider'] && 
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                }
              </React.Fragment>
            )}
            <Grid item xs={12} sm={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex' }}>
                  <Button variant="outlined" size="normal" style={buttonStyle} onClick={() => navigate(backUrl)}>
                    Back
                  </Button>
                </Box>
                {(purchaseOrderHistory.status === 'approved' || purchaseOrderHistory.status === 'received') && (
                  <LoadingButton
                    variant="outlined"
                    size="normal"
                    style={buttonStyle}
                    startIcon={<PictureAsPdfIcon />}
                    onClickPromise={() => downloadFile({ 
                      params: { id: purchaseOrderHistory.id, fileType: 'purchase_order_history/po_pdf'}, 
                      fileName: purchaseOrderHistory.po_number, 
                      fileExt: 'pdf' 
                    })}
                  >
                    Download
                  </LoadingButton>
                )}
              </Box>
            </Grid>
          </Grid>
        </StyledCardFormContent>
      </StyledCard>
    </Page>
  );
}